import { BaseWebComponentView } from '../../../../framework/BaseWebComponentView';
import { GzFormFieldWebComponent } from '../../../../ui/gzForm/formField';
import { FormGroup } from '../../../../ui/gzForm/formGroup/form-group';
import { getAccountsStrings } from '../../accountStrings/accountStrings';
import { GzTextWebComponent } from '../../../../ui/gzText';
import { GzButtonWebComponent } from '../../../../ui/gzButton';
import { GzDropdownWebComponent } from '../../../../ui/gzDropdown';
import { translateFn } from '../../../../utils/translator.helper';
import { GzDialogWebComponent } from '../../../../ui/gzDialog';
import { AccountDetails, AccountDetailsEvents, ADUser } from '../types';
import { RequiredValidator } from '../../../../ui/gzForm/validators/required.validator';
import { MaxLengthValidator } from '../../../../ui/gzForm/validators/max-length.validator';
import { AccountConstraints } from '../../constraints/AccountConstraints';
import { RegexValidator } from '../../../../ui/gzForm/validators/regex.validator';
import { MinLengthValidator } from '../../../../ui/gzForm/validators/min-length.validator';
import { CustomizableValidator } from '../../../../ui/gzForm/validators/customizable.validator';
import { EmailValidator } from '../../../../ui/gzForm/validators/email.validator';
import { SelectOption } from '../../../../ui/gzDropdown/types';
import { AccountActionContext } from '../../types/AccountActionContext';
import { RemoteValidator } from '../../../../ui/gzForm/validators/remote.validator';
import _ from 'lodash';
import { toggleVisibility } from '../../../../utils/htmlElement.utils';
import { GzToggleWebComponent } from '../../../../ui/gzToggle';
import { ToggleEvents } from '../../../../ui/gzToggle/types';
import {
  API_TYPE_AD_USERS_FOR_ACCOUNTS,
  API_TYPE_COMPANIES_FOR_ACCOUNTS,
  WEB_COMPONENT_COMPANY_SELECTOR
} from '../../../../webcomponents.constants';
import { GzSingleSelectComplexWebComponent } from '../../../../ui/gzSingleSelectComplex';
import { GetItemsResponse } from '../../../../ui/gzSingleSelectComplex/types';
import { AccountSource, AccountSourceId } from '../../types/AccountSources';
import { cloneDeep } from 'lodash';
import { GzInputWebComponent } from '../../../../ui/gzInput/component/GzInputWebComponent';
import { htmlDecode } from '../../../../utils/escape.utils';
import { CompanySelectorWebComponent } from '../../../../companySelector';
import { ValidationResult } from '../../../../ui/gzForm/formField/types/form-validator';
import { GzPhoneInputWebComponent } from "../../../../ui/gzPhoneInput";
import { PhoneNumber, PhoneNumberValidationStrings } from '../../../../ui/gzPhoneInput/types';
import { PhoneNumberValidator } from '../../../../ui/gzForm/validators/phone-number.validator';

export class AccountDetailsWebView extends BaseWebComponentView {
  public context: AccountActionContext;

  public formGroup: FormGroup;
  public dom: {
    $detailsSectionTitle: HTMLElement;
    $userId: GzFormFieldWebComponent;
    $importFromActiveDirectoryFormField: HTMLElement;
    $importFromActiveDirectoryToggle: GzToggleWebComponent;
    $importFromActiveDirectoryDescription: GzTextWebComponent;
    $activeDirectorySynchronizeButton: GzButtonWebComponent;
    $emailFormField: GzFormFieldWebComponent;
    $fullNameFormField: GzFormFieldWebComponent;
    $usernameFormField: GzFormFieldWebComponent;
    $usernameADFormFieldSelector: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;
    $companyFormField: GzFormFieldWebComponent<GzSingleSelectComplexWebComponent, string | number>;
    $passwordFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $confirmPasswordFormField: GzFormFieldWebComponent<GzInputWebComponent, string>;
    $confirmPasswordDescription: GzTextWebComponent;
    $timezoneFormField: GzFormFieldWebComponent;
    $timezoneDropdown: GzDropdownWebComponent;
    $languageDropdown: GzDropdownWebComponent;
    $languageFormField: GzFormFieldWebComponent;
    $companySelector: CompanySelectorWebComponent;
    $phoneNumberSelector: GzFormFieldWebComponent<GzPhoneInputWebComponent, PhoneNumber>;
    $phoneNumberInput: GzPhoneInputWebComponent;
  };

  getDomElements() {
    this.dom = {
      $detailsSectionTitle: this.shadow.querySelector('#details-section-title'),

      $userId: this.shadow.querySelector('#user-id'),
      $importFromActiveDirectoryFormField: this.shadow.querySelector('#import-from-active-directory'),
      $importFromActiveDirectoryToggle: this.shadow.querySelector('#import-from-active-directory-toggle'),
      $importFromActiveDirectoryDescription: this.shadow.querySelector('#import-from-active-directory-description'),
      $activeDirectorySynchronizeButton: this.shadow.querySelector('#ad-synchronize-button'),

      $emailFormField: this.shadow.querySelector('#email'),
      $fullNameFormField: this.shadow.querySelector('#fullname'),

      $usernameFormField: this.shadow.querySelector('#username'),
      $usernameADFormFieldSelector: this.shadow.querySelector('#username-ad-form-field'),

      $companyFormField: this.shadow.querySelector('#account-company-form-field'),

      $passwordFormField: this.shadow.querySelector('#password'),
      $confirmPasswordFormField: this.shadow.querySelector('#confirm-password'),
      $confirmPasswordDescription: this.shadow.querySelector('#confirm-password-description'),

      $timezoneFormField: this.shadow.querySelector('#timezone'),
      $timezoneDropdown: this.shadow.querySelector('#timezone-dropdown'),

      $languageDropdown: this.shadow.querySelector('#language-dropdown'),
      $languageFormField: this.shadow.querySelector('#language'),

      $companySelector: this.shadow.querySelector(WEB_COMPONENT_COMPANY_SELECTOR),

      $phoneNumberSelector: this.shadow.querySelector('#phone-number'),
      $phoneNumberInput: this.shadow.querySelector('#phone-number-input'),
    };

    this.getTranslations();
    this.setTranslatedStrings();
  }

  getTranslations() {
    this.translations = getAccountsStrings();
    return this.translations;
  }

  setTranslatedStrings(): void {
    this.dom.$detailsSectionTitle.innerText = this.translations.detailsTitle;
    this.dom.$importFromActiveDirectoryToggle.innerText = this.translations.importFromActiveDirectoryLabel;
    this.dom.$activeDirectorySynchronizeButton.innerText = this.translations.activeDirectorySynchronizeButton;
    this.dom.$importFromActiveDirectoryDescription.innerText = this.translations.importFromActiveDirectoryDescription;
    this.dom.$emailFormField.label = this.translations.emailLabel;
    this.dom.$fullNameFormField.label = this.translations.fullNameLabel;
    this.dom.$usernameFormField.label = this.translations.usernameLabel;

    this.dom.$usernameADFormFieldSelector.label = this.translations.usernameLabel;
    if (this.dom.$usernameADFormFieldSelector.Input) {
      this.dom.$usernameADFormFieldSelector.Input.placeholder = this.dependencies.translateFn('Select username.');
      this.dom.$usernameADFormFieldSelector.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$usernameADFormFieldSelector.Input.noSearchResultsString =
        this.dependencies.translateFn('No options found');
    }

    this.dom.$companyFormField.label = this.translations.companySelectorLabel;
    if (this.dom.$companyFormField.Input) {
      this.dom.$companyFormField.Input.placeholder = this.dependencies.translateFn('Choose company');
      this.dom.$companyFormField.Input.searchPlaceholderString = this.dependencies.translateFn('Search');
      this.dom.$companyFormField.Input.noSearchResultsString = this.dependencies.translateFn('No options found');
    }

    this.dom.$passwordFormField.label = this.translations.passwordLabel;
    this.dom.$passwordFormField.Input.placeholder = this.translations.passwordPlaceHolder;
    this.dom.$confirmPasswordFormField.label = this.translations.confirmPasswordLabel;
    this.dom.$confirmPasswordFormField.Input.placeholder = this.translations.confirmPasswordPlaceholder;
    this.dom.$confirmPasswordDescription.innerText = this.translations.confirmPasswordDescription;
    this.dom.$timezoneFormField.label = this.translations.timezoneLabel;
    this.dom.$languageFormField.label = this.translations.languageLabel;

    this.dom.$phoneNumberSelector.label = this.translations.phoneNumberLabel;

    this.dom.$phoneNumberInput.strings = {
      numberPlaceholder: this.translations.phoneNumberLabel,
      emptyCodeValuePlaceholder: this.translations.phoneNumberLabel,
      phoneCodeLabel: this.translations.countrySelectorLabel,
      label: "",
      noSearchResultsString: this.commonStrings.noResultsFound,
      searchPlaceholderString: this.commonStrings.search
    }
  }

  disconnected() {}

  async buildSuccessView(viewData: AccountDetails): Promise<void> {
    this.setInitialValues();
    this.setCompanyDropdown(viewData);
    this.setADUserDropdown(viewData);
    this.showFieldsDependingOnPlatform();
    this.resetData();
    this.createFormGroup();
    this.setupTimezoneSelectionDropdown(viewData);
    this.setupLanguageSelectionDropdown(viewData);
    this.showImportFromActiveDirectory(viewData);

    if (this.context === AccountActionContext.Edit) {
      this.setFormGroupValues(viewData);
    }
    this.togglePasswordFieldsAsRequired();
  }

  private showFieldsDependingOnPlatform(): void {
    // on-premise
    if (!this.dependencies.isCloud()) {
      toggleVisibility(this.dom.$usernameADFormFieldSelector, false);
      toggleVisibility(this.dom.$companyFormField, false);
      toggleVisibility(this.dom.$usernameFormField, true);
      toggleVisibility(this.dom.$passwordFormField, true);
      toggleVisibility(this.dom.$confirmPasswordFormField, true);
      toggleVisibility(this.dom.$confirmPasswordDescription, true);
      toggleVisibility(this.dom.$phoneNumberSelector, false);
      return;
    }

    // cloud
    if (this.dependencies.permissions.canHaveChildCompanies) {
      toggleVisibility(this.dom.$companyFormField, true);
    }
    toggleVisibility(this.dom.$usernameFormField, false);
    toggleVisibility(this.dom.$usernameADFormFieldSelector, false);

    if (this.context === AccountActionContext.Create) {
      toggleVisibility(this.dom.$passwordFormField, false);
      toggleVisibility(this.dom.$confirmPasswordFormField, false);
      toggleVisibility(this.dom.$confirmPasswordDescription, false);
    }
  }

  private setInitialValues(): void {
    this.dom.$fullNameFormField.initialValue = '';
    this.dom.$usernameFormField.initialValue = '';
    this.dom.$emailFormField.initialValue = '';
    this.dom.$passwordFormField.initialValue = '';
    this.dom.$confirmPasswordFormField.initialValue = '';
    this.dom.$timezoneFormField.initialValue = [];
    this.dom.$languageFormField.initialValue = [];
    this.dom.$phoneNumberSelector.initialValue = {
      countryCode: '',
      subscriberNumber: ''
    };
  }

  private resetData(): void {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.applyToFormGroupFields(field => {
      if (field instanceof GzFormFieldWebComponent) {
        field.discardChanges();
        field.resetField();
      }
    });
  }

  public discardFormGroupChangesOnADUser(): void {
    if (!this.formGroup) {
      return;
    }

    this.formGroup.applyToFormGroupFields(field => {
      if (
        field instanceof GzFormFieldWebComponent &&
        (field === this.dom.$usernameFormField ||
          field === this.dom.$usernameADFormFieldSelector ||
          field === this.dom.$emailFormField ||
          field === this.dom.$fullNameFormField ||
          field === this.dom.$passwordFormField ||
          field === this.dom.$confirmPasswordFormField)
      ) {
        field.discardChanges();
        field.resetField();
      }
    });
  }

  private setFormFieldsValidators(): void {
    this.setUsernameValidator();
    this.setUsernameADValidator();
    this.setEmailValidator();
    this.setFullNameValidator();
    this.setPasswordValidator();
    this.setCompanyValidators();
    this.setPhoneNumberValidators();
  }

  private getADUserLabelByUserId(adUserId: string): string {
    for (const user of this.dom.$usernameADFormFieldSelector.Input.filterOptions) {
      if (user.value === adUserId) {
        return user.label;
      }
    }

    return '';
  }

  private setPhoneNumberValidators(){
      const validationTexts: PhoneNumberValidationStrings = {
        mandatoryErrorText: this.translations.phoneNumberRequiredValidation,
        invalidInputValueErrorText: this.translations.phoneNumberInvalidSubscriberNumber,
        invalidPhoneErrorText: this.translations.phoneNumberInvalid,
      };

      const isRequired = false;
      const phoneNumberValidator = PhoneNumberValidator({ isRequired, validationTexts });
      this.dom.$phoneNumberSelector.Validators = [phoneNumberValidator];
  }

  private setUsernameValidator(): void {
    if (this.dependencies.isCloud()) {
      return;
    }

    this.dom.$usernameFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MinLengthValidator(AccountConstraints.Username.MinLength, this.translations.userNameMinLength),
      MaxLengthValidator(AccountConstraints.Username.MaxLength, this.translations.userNameMaxLength),
      RemoteValidator(
        (value: string) => this.dependencies.api.clients.Users.checkIfUserNameIsValid(value, this.dom.$userId.value),
        (response: { success: boolean }) => response.success,
        this.translations.usernameAlreadyExists,
        this.translations.communicationError,
        500
      ),
      RegexValidator(
        /^[^",\/\\\[\]\:\|<>\+=;\?\*\u0000 - \u001F]+[^\.",\/\\\[\]\:\|<>\+=;\?\*\u0000 - \u001F]$/i,
        this.translations.invalidUserName
      ),
    ];
  }

  private setUsernameADValidator(): void {
    if (this.dependencies.isCloud()) {
      return;
    }

    this.dom.$usernameADFormFieldSelector.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(AccountConstraints.Username.MaxLength, this.translations.userNameMaxLength),
      RemoteValidator(
        (adUserId: string) =>
          this.dependencies.api.clients.Users.checkIfUserNameIsValid(
            this.getADUserLabelByUserId(adUserId),
            this.dom.$userId.value
          ),
        (response: { success: boolean }) => response.success,
        this.translations.usernameAlreadyExists,
        this.translations.communicationError
      ),
      RegexValidator(
        /^[^",\/\\\[\]\:\|<>\+=;\?\*\u0000 - \u001F]+[^\.",\/\\\[\]\:\|<>\+=;\?\*\u0000 - \u001F]$/i,
        this.translations.invalidUserName
      ),
    ];
  }

  private setEmailValidator(): void {
    this.dom.$emailFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(AccountConstraints.Email.MaxLength, this.translations.emailMaxLength),
      EmailValidator(this.translations.invalidEmail),
      RemoteValidator(
        (value: string) => this.dependencies.api.clients.Users.checkIfEmailIsValid(value, this.dom.$userId.value),
        (response: { success: boolean }) => response.success,
        this.translations.emailAlreadyExists,
        this.translations.communicationError,
        500
      ),
    ];
  }

  private setFullNameValidator(): void {
    this.dom.$fullNameFormField.Validators = [
      RequiredValidator(this.commonStrings.requiredField),
      MaxLengthValidator(AccountConstraints.FullName.MaxLength, this.translations.fullNameMaxLength),
      RemoteValidator(
        (value: string) => this.dependencies.api.clients.Users.checkIfFullNameIsValid(value),
        (response: { success: boolean }) => response.success,
        this.translations.fullNameInvalidCharacters,
        this.translations.communicationError,
        500
      ),
    ];
  }

  public setPasswordValidator(): void {
    const defaultPasswordFormFieldValidators = [
      MaxLengthValidator(AccountConstraints.Password.MaxLength, this.translations.passwordMaxLength),
      RegexValidator(
        /^(?=.*?[A-Z])(?=.*?[a-z])(?=.*?[0-9])(?=.*?[~!@#$%^&*_\-+=`|(){}\[\]:;"'<>,.?\/]).*$/,
        this.translations.invalidPassword
      ),
      CustomizableValidator(this.customMinLengthValidation.bind(this)),
    ];

    const defaultConfirmPasswordFormFieldValidators = [
      CustomizableValidator(value => {
        if (value !== this.dom.$passwordFormField.value) {
          return {
            valid: false,
            error: this.translations.passwordsDoNotMatch,
          };
        }
        return {
          valid: true,
        };
      }),
    ];

    if (this.context === AccountActionContext.Create && !this.dependencies.isCloud()) {
      this.dom.$passwordFormField.Validators = [...defaultPasswordFormFieldValidators];

      const confirmPasswordFormFieldValidators = cloneDeep(defaultConfirmPasswordFormFieldValidators);
      confirmPasswordFormFieldValidators.push(RequiredValidator(this.commonStrings.requiredField));
      this.dom.$confirmPasswordFormField.Validators = confirmPasswordFormFieldValidators;
    }

    if (this.context === AccountActionContext.Edit) {
      // on-cloud, on-premise edit -> password is not mandatory
      this.dom.$confirmPasswordFormField.Validators = cloneDeep(defaultConfirmPasswordFormFieldValidators);

      const passwordFormFieldValidators = cloneDeep(defaultPasswordFormFieldValidators);
      passwordFormFieldValidators.push(
        RemoteValidator(
          (value: string) => this.dependencies.api.clients.Users.checkPasswordHistory(value, this.dom.$userId.value),
          (response: { success: boolean }) => response.success,
          this.translations.passwordUsedInThePast,
          this.translations.communicationError
        )
      );
      this.dom.$passwordFormField.Validators = passwordFormFieldValidators;
    }
  }

  private async customMinLengthValidation(): Promise<ValidationResult> {
    if (!this.dom.$passwordFormField.value.length) {
      return {
        valid: true,
      };
    }

    const validator = MinLengthValidator(AccountConstraints.Password.MinLength, this.translations.passwordMinLength);
    return await validator(this.dom.$passwordFormField.value);
  }

  private setCompanyValidators(): void {
    this.dom.$companyFormField.Validators = [RequiredValidator(this.commonStrings.requiredField)];
  }

  private setFormGroupValues(viewData: AccountDetails): void {
    this.formGroup.value = {
      userId: _.get(viewData, ['_id'], ''),
      email: htmlDecode(_.get(viewData, ['email'], '')),
      userName: htmlDecode(_.get(viewData, ['userName'], '')),
      fullName: htmlDecode(_.get(viewData, ['profile', 'fullName'], '')),
      password: '',
      confirmPassword: '',
      timezone: _.get(viewData, ['profile', 'timezone'], ''),
      language: _.get(viewData, ['profile', 'language'], ''),
    };

    const phoneNumber = {
      countryCode: viewData.phoneNumber?.countryCode ?? '',
      subscriberNumber: viewData.phoneNumber?.subscriberNumber ?? ''
    } as PhoneNumber;

    this.dom.$phoneNumberInput.setFormValue(phoneNumber);

    const accountSource = _.get(viewData, ['source'], AccountSource.MANUAL);
    if (!this.dependencies.isCloud() && accountSource !== AccountSource.MANUAL) {
      this.changeFormFieldsVisibilityDependingOnSourceAccount(accountSource);
    }
  }

  public createFormGroup(): void {
    this.formGroup = new FormGroup(this.getFormGroupItems());
    this.setFormFieldsValidators();
  }

  public getFormGroup(): FormGroup {
    return this.formGroup;
  }

  private getFormGroupItems() {
    const formGroupItems: {} = {};

    if (this.context === AccountActionContext.Edit) {
      formGroupItems['userId'] = this.dom.$userId;
    }

    if (!this.dependencies.isCloud()) {
      formGroupItems['userName'] = this.dom.$usernameFormField;
    }

    if (this.dependencies.isCloud()) {
      formGroupItems['phoneNumber'] = this.dom.$phoneNumberSelector;
    }

    // on-premise cloud does not have password fields.. skip in this case
    if (!(this.context === AccountActionContext.Create && this.dependencies.isCloud())) {
      formGroupItems['password'] = this.dom.$passwordFormField;
      formGroupItems['confirmPassword'] = this.dom.$confirmPasswordFormField;
      this.dom.$phoneNumberSelector.Input.markAsValid();
      this.dom.$phoneNumberSelector.Input.setEnabled(true);
    }

    if (
      this.dependencies.isCloud() &&
      this.context === AccountActionContext.Create &&
      this.dependencies.permissions.canHaveChildCompanies
    ) {
      formGroupItems['company'] = this.dom.$companyFormField;
    }

    formGroupItems['email'] = this.dom.$emailFormField;
    formGroupItems['fullName'] = this.dom.$fullNameFormField;
    formGroupItems['timezone'] = this.dom.$timezoneFormField;
    formGroupItems['language'] = this.dom.$languageFormField;

    return formGroupItems;
  }

  private resyncADUsers(): void {
    this.dom.$activeDirectorySynchronizeButton.dispatchEvent(
      new CustomEvent(AccountDetailsEvents.RESYNC_AD_IN_PROGRESS, {
        bubbles: true,
        composed: true,
      })
    );
  }

  public openSyncWarning(): void {
    this.dom.$activeDirectorySynchronizeButton.disabled = true;
    const windowBodyHtml = `
      <p>
        ${translateFn('Resyncing the Active Directory users may take some time. Do you want to continue?')}
      </p>`;

    const windowFooterHtml = `<gz-button id='confirm' type='outline' color='negative' class='mr-2'>
       ${translateFn('Yes')}
      </gz-button>
      <gz-button id='cancel' type='outline' color='secondary'>
        ${translateFn('No')}
      </gz-button>`;

    const windowTitle = translateFn('Warning');
    const cancelCallback = () => {
      if (this.dom.$activeDirectorySynchronizeButton) {
        this.dom.$activeDirectorySynchronizeButton.disabled = false;
      }
    };
    const confirmCallback = async () => {
      this.resyncADUsers();
    };

    GzDialogWebComponent.computeCustomDialog(
      windowBodyHtml,
      windowFooterHtml,
      windowTitle,
      cancelCallback,
      confirmCallback
    );
  }

  public changeFormFieldsVisibilityDependingOnSourceAccount(source: AccountSourceId): void {
    if (source === AccountSource.ACTIVEDIR) {
      if (this.context === AccountActionContext.Create) {
        toggleVisibility(this.dom.$importFromActiveDirectoryFormField, true);
        toggleVisibility(this.dom.$activeDirectorySynchronizeButton, true);
      }

      toggleVisibility(this.dom.$usernameADFormFieldSelector, true);
      toggleVisibility(this.dom.$usernameFormField, false);
      this.dom.$fullNameFormField.disabled = true;
      this.dom.$emailFormField.disabled = true;
      this.dom.$emailFormField.removeAttribute('required');
      this.dom.$passwordFormField.disabled = true;
      this.dom.$confirmPasswordFormField.disabled = true;
      toggleVisibility(this.dom.$confirmPasswordDescription, false);
    } else {
      toggleVisibility(this.dom.$usernameADFormFieldSelector, false);
      toggleVisibility(this.dom.$usernameFormField, true);
      toggleVisibility(this.dom.$activeDirectorySynchronizeButton, false);
      toggleVisibility(this.dom.$importFromActiveDirectoryDescription, false);
      this.dom.$emailFormField.disabled = false;
      this.dom.$emailFormField.setAttribute('required', 'true');
      this.dom.$fullNameFormField.disabled = false;
      this.dom.$passwordFormField.disabled = false;
      this.dom.$confirmPasswordFormField.disabled = false;
      toggleVisibility(this.dom.$confirmPasswordDescription, true);
    }

    this.changeValidationsDependingOnSourceAccount(source);
  }

  private changeValidationsDependingOnSourceAccount(source: AccountSourceId): void {
    if (source === AccountSource.ACTIVEDIR) {
      this.formGroup.addFormChild(['userNameAD'], this.dom.$usernameADFormFieldSelector);
      this.formGroup.removeFormChild(['username']);
      this.formGroup.removeFormChild(['email']);
      this.formGroup.removeFormChild(['fullName']);
      this.formGroup.removeFormChild(['password']);
      this.formGroup.removeFormChild(['confirmPassword']);

      this.setUsernameADValidator();
      this.removeValidatorsForField(this.dom.$usernameFormField);
      this.removeValidatorsForField(this.dom.$emailFormField);
      this.removeValidatorsForField(this.dom.$fullNameFormField);
      this.removeValidatorsForField(this.dom.$passwordFormField);
      this.removeValidatorsForField(this.dom.$confirmPasswordFormField);

      return;
    }

    this.formGroup.removeFormChild(['userNameAD']);
    this.formGroup.addFormChild(['email'], this.dom.$emailFormField);
    this.formGroup.addFormChild(['fullName'], this.dom.$fullNameFormField);
    this.formGroup.addFormChild(['password'], this.dom.$passwordFormField);
    this.formGroup.addFormChild(['confirmPassword'], this.dom.$confirmPasswordFormField);

    this.setUsernameValidator();
    this.setEmailValidator();
    this.setFullNameValidator();
    this.setPasswordValidator();
    this.removeValidatorsForField(this.dom.$usernameADFormFieldSelector);
  }

  public removeValidatorsForField(field) {
    field['Validators'] = [];
  }

  public resetFormFieldsOnSourceAccountChange(source: AccountSourceId): void {
    this.dom.$usernameADFormFieldSelector.value = '';
    this.dom.$usernameFormField.value = '';
    this.dom.$emailFormField.value = '';
    this.dom.$fullNameFormField.value = '';
    this.dom.$passwordFormField.value = '';
    this.dom.$confirmPasswordFormField.value = '';

    if (source === AccountSource.ACTIVEDIR) {
      this.dom.$passwordFormField.value = '******';
      this.dom.$confirmPasswordFormField.value = '******';
    }
  }

  public fillUserDetails(adUser: ADUser): void {
    this.dom.$emailFormField.value = adUser.email;
    this.dom.$fullNameFormField.value = adUser.fullName;
    this.dom.$usernameFormField.value = adUser.userName;
  }

  private setupTimezoneSelectionDropdown(viewData: AccountDetails): void {
    this.dom.$timezoneDropdown.options = viewData.timezoneOptions
      ? viewData.timezoneOptions.map(timezone => {
          return {
            id: timezone.id,
            title: timezone.value,
            value: timezone.id,
          } as SelectOption;
        })
      : [];

    if (this.context === AccountActionContext.Create) {
      // by default select logged-in user timezone
      this.selectTimezoneTarget(this.dependencies.getTimezone());
    }
  }

  private selectTimezoneTarget(timezone: string): void {
    // find option that match the target timezone
    const matchedOption = this.dom.$timezoneDropdown.options.find(option => option.value === timezone) as SelectOption;

    if (!matchedOption) {
      return;
    }

    this.dom.$timezoneDropdown.selectOption(matchedOption);
    this.dom.$timezoneFormField.value = matchedOption.value;
  }

  private setupLanguageSelectionDropdown(viewData: AccountDetails): void {
    this.dom.$languageDropdown.options = viewData.languageOptions
      ? viewData.languageOptions.map(language => {
          return {
            id: language.id,
            title: language.title,
            value: language.id,
          } as SelectOption;
        })
      : [];

    if (this.context === AccountActionContext.Create) {
      // by default select logged-in user language
      this.selectLanguageTarget(this.dependencies.getUserLanguage());
    }
  }

  private selectLanguageTarget(language: string): void {
    // find option that match the target language
    const matchedOption = this.dom.$languageDropdown.options.find(option => option.value === language) as SelectOption;

    if (!matchedOption) {
      return;
    }

    this.dom.$languageDropdown.selectOption(matchedOption);
    this.dom.$languageFormField.value = matchedOption.value;
  }

  private showImportFromActiveDirectory(viewData: AccountDetails): void {
    if (this.dependencies.isCloud()) {
      return;
    }

    if (this.context !== AccountActionContext.Create || !viewData.adIntegration) {
      return;
    }

    toggleVisibility(this.dom.$importFromActiveDirectoryFormField, true);

    this.dom.$importFromActiveDirectoryToggle.dispatchEvent(
      new CustomEvent(ToggleEvents.TOGGLE_CHANGE, {
        detail: {
          checked: true,
        },
        bubbles: true,
        composed: true,
      })
    );
  }

  private setCompanyDropdown(viewData: AccountDetails): void {
    if (this.dependencies.permissions.canHaveChildCompanies) {
      toggleVisibility(this.dom.$companyFormField, true);
      this.dom.$companyFormField.removeAttribute('disabled');

      this.dom.$companyFormField.Input.setApiType(API_TYPE_COMPANIES_FOR_ACCOUNTS);

      const companyData: GetItemsResponse = {
        items: [],
        totalItemsCount: 0,
      };
      if (viewData && viewData.companyInfo) {
        companyData.items.push({
          label: viewData.companyInfo.name,
          value: viewData.companyInfo._id,
        });
        (companyData.totalItemsCount as number) += 1;
      }

      this.dom.$companyFormField.Input.updateComplete.then(() => {
        this.dom.$companyFormField.Input.controller
          .setFilterOptions(companyData)
          .then(() => {
            if (this.context === AccountActionContext.Edit) {
              this.dom.$companyFormField.Input.setFormValue(viewData.companyInfo._id as string);
              this.dom.$companyFormField.disabled = true;
            }
          })
          .catch();
      });
    } else {
      toggleVisibility(this.dom.$companyFormField, false);
      this.dom.$companyFormField.setAttribute('disabled', '');
    }
  }

  private setADUserDropdown(viewData: AccountDetails): void {
    if (this.dependencies.isCloud()) {
      return;
    }

    const accountSource = _.get(viewData, ['source'], AccountSource.MANUAL);
    if (this.context === AccountActionContext.Edit && accountSource !== AccountSource.ACTIVEDIR) {
      return;
    }

    let adUsername = '';
    if (this.context === AccountActionContext.Edit) {
      adUsername = _.get(viewData, ['userName'], '');
    }
    this.fillADUserDropdown(adUsername).then().catch();
  }

  public getSelectedCompanyId(): string | number {
    if (!this.dependencies.permissions.canHaveChildCompanies) {
      return this.dependencies.getUser().company.id;
    }

    return this.dom.$companyFormField.value;
  }

  public togglePasswordFieldsAsRequired(): void {
    const required = !this.dependencies.isCloud() && this.context === AccountActionContext.Create;

    if (!required) {
      this.dom.$passwordFormField.removeAttribute('required');
      this.dom.$confirmPasswordFormField.removeAttribute('required');
      return;
    }

    this.dom.$passwordFormField.setAttribute('required', '');
    this.dom.$confirmPasswordFormField.setAttribute('required', '');
  }

  public async fillADUserDropdown(adUsername: string = ''): Promise<void> {
    toggleVisibility(this.dom.$usernameADFormFieldSelector, true);
    this.dom.$usernameADFormFieldSelector.removeAttribute('disabled');

    // reset filter options
    this.dom.$usernameADFormFieldSelector.Input.filterOptions = [];

    this.dom.$usernameADFormFieldSelector.Input.setApiType(API_TYPE_AD_USERS_FOR_ACCOUNTS);

    const userData: GetItemsResponse = {
      items: [],
      totalItemsCount: 0,
    };

    await this.dom.$usernameADFormFieldSelector.Input.updateComplete;
    await this.dom.$usernameADFormFieldSelector.Input.controller.setFilterOptions(userData);
    if (!adUsername) {
      return;
    }

    this.dom.$usernameADFormFieldSelector.disabled = true;

    if (this.dom.$usernameADFormFieldSelector.Input.controller.totalItemsCount) {
      const adUsers = this.dom.$usernameADFormFieldSelector.Input.filterOptions;
      for (const user of adUsers) {
        if (user.label === adUsername) {
          this.dom.$usernameADFormFieldSelector.Input.setFormValue(user.value);
          break;
        }
      }
    }
  }

  public togglePasswordFields(showFields: boolean): void {
    if (showFields) {
      toggleVisibility(this.dom.$passwordFormField, true);
      toggleVisibility(this.dom.$confirmPasswordFormField, true);
      toggleVisibility(this.dom.$confirmPasswordDescription, true);
      this.setPasswordValidator();
      return;
    }

    this.dom.$passwordFormField.value = '';
    this.dom.$confirmPasswordFormField.value = '';
    toggleVisibility(this.dom.$passwordFormField, false);
    toggleVisibility(this.dom.$confirmPasswordFormField, false);
    toggleVisibility(this.dom.$confirmPasswordDescription, false);
    this.removeValidatorsForField(this.dom.$passwordFormField);
    this.removeValidatorsForField(this.dom.$confirmPasswordFormField);
  }
}
